import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import api from '../api/client';
import { useAuth } from '../context/AuthContext';
import { usePermission } from '../context/PermissionContext';
import ItemPickerModal from '../components/ItemPickerModal';
import { SpecEditor, HwSpecEditor } from './EstimateNewPage';
import { deriveDetailSections } from '../utils/detailSections';
import { matchAnyCI } from '../utils/search';

const fmtN = (n) => n == null || n === '' ? '' : Number(n).toLocaleString();
const pN = (s) => parseInt(String(s).replace(/[^0-9-]/g, '')) || 0;

const TYPE_LABEL = { software:'Software', license:'License', hardware:'Hardware', appliance:'Appliance' };
const TYPE_SHORT = { software:'SW', license:'LIC', hardware:'HW', appliance:'AP' };
const TYPE_BADGE = { software:'badge-blue', license:'badge-purple', hardware:'badge-gray', appliance:'badge-green' };

// 묶음 제안 작성 / 수정 / 수정-후-채택 겸용
export default function BundleNewPage() {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const { user: authUser } = useAuth();
  const { canPerformAction } = usePermission();
  const canEdit = canPerformAction('묶음 등록/편집');

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [visibility, setVisibility] = useState('public');
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);
  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(null), 2000); };

  // 항목 표 토글
  const [showCP, setShowCP] = useState(true);
  const [showCPA, setShowCPA] = useState(false);

  // 인라인 편집
  const [editingDetail, setEditingDetail] = useState(null);   // { idx, section }
  const [editingHwIdx, setEditingHwIdx] = useState(null);

  // 추가 모달
  const [showAddProd, setShowAddProd] = useState(false);
  const [showAddPart, setShowAddPart] = useState(false);
  const [showItemPicker, setShowItemPicker] = useState(false);
  const [addSearch, setAddSearch] = useState('');
  const [addType, setAddType] = useState('all');

  const [products, setProducts] = useState([]);
  const [allParts, setAllParts] = useState([]);

  useEffect(() => {
    api.get('/products?status=active').then(setProducts).catch(()=>{});
    api.get('/parts').then(d => setAllParts((d||[]).filter(p => p.purchase_price != null && p.is_part !== false))).catch(()=>{});
  }, []);

  // 즐겨찾기
  const [favorites, setFavorites] = useState(() => {
    const f = authUser?.preferences?.favorites;
    return { products: f?.products || [], parts: f?.parts || [] };
  });
  useEffect(() => {
    const f = authUser?.preferences?.favorites;
    if (f) setFavorites({ products: f.products || [], parts: f.parts || [] });
  }, [authUser]);
  const toggleFavorite = async (kind, fid) => {
    const list = favorites[kind] || [];
    const next = list.includes(fid) ? list.filter(x => x !== fid) : [...list, fid];
    const merged = { ...favorites, [kind]: next };
    setFavorites(merged);
    try { await api.put('/auth/preferences', { favorites: merged }); } catch(e) {}
  };

  // 수정 모드 로드
  const load = useCallback(async () => {
    if (!isEdit) return;
    try {
      const b = await api.get(`/bundles/${id}`);
      setName(b.name || '');
      setDescription(b.description || '');
      setVisibility(b.visibility || 'public');
      // 항목 — product 메타 fetch + _editSpecs/_editFeatures 복원
      const its = Array.isArray(b.items) ? b.items : [];
      const restored = [];
      for (const it of its) {
        const item = {
          ...it,
          unit_price: String(it.unit_price ?? 0),
          bundle: it.bundle || '',
          _editSpecs: it.specs || [],
          _editFeatures: it.features || [],
        };
        if (it.item_type === 'product' && it.product_id) {
          try { item._product = await api.get(`/products/${it.product_id}`); } catch(e) {}
        }
        restored.push(item);
      }
      setItems(restored);
    } catch (e) { alert(e.message); navigate('/bundles'); }
  }, [id, isEdit, navigate]);
  useEffect(() => { load(); }, [load]);

  // 다음 사용 가능한 Appliance 묶음 라벨 — "A1", "A2", ...
  const nextApplianceLabel = (its) => {
    const used = new Set(its.map(it => it.bundle).filter(Boolean));
    for (let n = 1; n < 1000; n++) {
      const label = `A${n}`;
      if (!used.has(label)) return label;
    }
    return `A${Date.now()}`;
  };

  // Appliance 펼침 (BundleNewPage 한정 — _appliance_ref_id 는 비-persist 메타)
  const expandAppliance = async (p) => {
    const appl = await api.get(`/products/${p.id}`);
    if (!appl || !Array.isArray(appl.components) || appl.components.length === 0) {
      const prod = appl || p;
      setItems(prev => [...prev, {
        item_type:'product', product_id:p.id, item_name:prod.name, item_model:prod.model||'',
        main_title:prod.main_title||'', sub_description:prod.sub_description||'', sw_name:prod.sw_name||'',
        specs:prod.specs||[], features:prod.features||[], detail_sections: deriveDetailSections(prod), hw_specs:{},
        consumer_price:prod.consumer_price||null,
        unit_price:String(prod.price_end_user||prod.consumer_price||0),
        quantity:1, cost_per_unit:parseInt(prod.total_cost)||0, bundle:'',
        _product:appl, _editSpecs:prod.specs||[], _editFeatures:prod.features||[],
      }]);
      showToast(`Appliance "${prod.name}" 에 자식 상품이 없어 단일 행으로 추가됨`);
      return;
    }
    const label = nextApplianceLabel(items);
    const apConsumer = Number(appl.consumer_price) || 0;
    const apEndUser  = Number(appl.price_end_user) || 0;
    const apDistr    = Number(appl.price_distributor) || 0;
    const apPartner  = Number(appl.price_partner) || 0;
    const apCost     = Number(appl.total_cost) || 0;
    const useAppl    = apEndUser > 0 || apConsumer > 0 || apDistr > 0 || apPartner > 0;
    const rows = [];
    for (let i = 0; i < appl.components.length; i++) {
      const c = appl.components[i];
      let childProd = null;
      try { childProd = await api.get(`/products/${c.child_product_id}`); }
      catch(e) { console.error('자식 상품 상세 로드 실패', c.child_product_id, e); }
      const ch = childProd || c;
      const isFirst = (i === 0);
      const rowConsumer = useAppl ? (isFirst ? apConsumer : null) : (ch.consumer_price || null);
      const rowUnit     = useAppl ? (isFirst ? String(apEndUser || apConsumer || 0) : '0')
                                  : String(ch.price_end_user || ch.consumer_price || 0);
      const rowCost     = useAppl ? (isFirst ? apCost : 0) : (parseInt(ch.total_cost) || 0);
      const productForDisplay = useAppl
        ? { ...(childProd || c), consumer_price: apConsumer, price_end_user: apEndUser, price_distributor: apDistr, price_partner: apPartner }
        : (childProd || c);
      rows.push({
        item_type:'product', product_id:c.child_product_id,
        item_name:ch.name, item_model:ch.model||'',
        main_title:ch.main_title||'', sub_description:ch.sub_description||'', sw_name:ch.sw_name||'',
        specs:ch.specs||[], features:ch.features||[], detail_sections: deriveDetailSections(ch), hw_specs:{},
        consumer_price:rowConsumer,
        unit_price:rowUnit,
        quantity:c.quantity||1,
        cost_per_unit:rowCost,
        bundle:label,
        _appliance_ref_id:p.id,
        _appliance_name:appl.name,
        _product:productForDisplay,
        _editSpecs:ch.specs||[], _editFeatures:ch.features||[],
      });
    }
    setItems(prev => [...prev, ...rows]);
    showToast(`Appliance "${appl.name}" (자식 ${rows.length}개) 추가됨 — 묶음 ${label}`);
  };

  // 항목 추가 / 변경
  const addProduct = async (p) => {
    setShowAddProd(false); setAddSearch(''); setAddType('all');
    if (p.product_type === 'appliance') {
      await expandAppliance(p);
      return;
    }
    let full = null;
    try { full = await api.get(`/products/${p.id}`); } catch(e) {}
    const prod = full || p;
    setItems(prev => [...prev, {
      item_type: 'product',
      product_id: p.id,
      item_name: prod.name,
      item_model: prod.model || '',
      main_title: prod.main_title || '',
      sub_description: prod.sub_description || '',
      sw_name: prod.sw_name || '',
      specs: prod.specs || [],
      features: prod.features || [],
      detail_sections: deriveDetailSections(prod),
      hw_specs: {},
      consumer_price: prod.consumer_price || null,
      unit_price: String(prod.price_end_user || prod.consumer_price || 0),
      quantity: 1,
      cost_per_unit: parseInt(prod.total_cost) || 0,
      bundle: '',
      _product: full,
      _editSpecs: prod.specs || [],
      _editFeatures: prod.features || [],
    }]);
  };
  const addPart = (p) => {
    setItems(prev => [...prev, {
      item_type: 'part',
      part_id: p.id,
      item_name: p.name,
      item_model: p.spec || '',
      description: p.spec || '',
      consumer_price: null,
      unit_price: String(p.purchase_price || 0),
      quantity: 1,
      cost_per_unit: p.purchase_price || 0,
      bundle: '',
    }]);
    setShowAddPart(false); setAddSearch('');
  };
  const addManual = () => {
    setItems(prev => [...prev, {
      item_type: 'manual',
      item_name: '',
      item_model: '',
      description: '',
      consumer_price: null,
      unit_price: '0',
      quantity: 1,
      cost_per_unit: 0,
      bundle: '',
    }]);
  };

  const removeItem = (i) => {
    const it = items[i];
    if (it && it._appliance_ref_id) {
      if (!window.confirm('Appliance 묶음은 원자 단위입니다. 같은 묶음 행 전체가 함께 삭제됩니다. 진행할까요?')) return;
      setItems(items.filter(x => !(x._appliance_ref_id === it._appliance_ref_id && x.bundle === it.bundle)));
      return;
    }
    setItems(items.filter((_,idx)=>idx!==i));
  };
  const upItem = (i,k,v) => setItems(items.map((it,idx)=>idx===i?{...it,[k]:v}:it));
  const saveDetailEdit = (idx, specs, features) => {
    setItems(items.map((it,i)=>i===idx?{...it, _editSpecs:specs, _editFeatures:features, specs, features}:it));
    setEditingDetail(null);
  };
  const saveHwSpec = (idx, customBom) => {
    const newCost = customBom.reduce((s, b) => s + (parseInt(b.purchase_price)||0) * (parseInt(b.quantity)||1), 0);
    setItems(items.map((it, i) => i === idx ? {
      ...it,
      hw_specs: { ...it.hw_specs, custom_bom: customBom },
      cost_per_unit: newCost
    } : it));
    setEditingHwIdx(null);
  };
  // 항목 ↑↓ — 같은 bundle 라벨 그룹은 함께 이동
  const moveItem = (idx, dir) => {
    setItems(prev => {
      const bundle = prev[idx]?.bundle;
      let gs = idx, ge = idx;
      if (bundle) {
        while (gs > 0 && prev[gs-1].bundle === bundle) gs--;
        while (ge < prev.length-1 && prev[ge+1].bundle === bundle) ge++;
      }
      if (dir === -1) {
        if (gs === 0) return prev;
        const adj = prev[gs-1].bundle;
        let as = gs-1;
        if (adj) while (as > 0 && prev[as-1].bundle === adj) as--;
        return [...prev.slice(0, as), ...prev.slice(gs, ge+1), ...prev.slice(as, gs), ...prev.slice(ge+1)];
      }
      if (ge === prev.length-1) return prev;
      const adj = prev[ge+1].bundle;
      let ae = ge+1;
      if (adj) while (ae < prev.length-1 && prev[ae+1].bundle === adj) ae++;
      return [...prev.slice(0, gs), ...prev.slice(ge+1, ae+1), ...prev.slice(gs, ge+1), ...prev.slice(ae+1)];
    });
  };

  // 묶음 자체의 cleanItems
  const cleanItems = () => items.map(it => ({
    item_type:it.item_type, product_id:it.product_id||null, part_id:it.part_id||null,
    item_name:it.item_name, item_model:it.item_model, main_title:it.main_title||'', sub_description:it.sub_description||'', sw_name:it.sw_name||'',
    specs:it._editSpecs||it.specs||[], features:it._editFeatures||it.features||[],
    detail_sections: Array.isArray(it.detail_sections) ? it.detail_sections : [],
    hw_specs:it.hw_specs||{}, description:it.description||'',
    consumer_price:it.consumer_price?parseInt(it.consumer_price):null,
    unit_price:pN(it.unit_price), quantity:parseInt(it.quantity)||1,
    cost_per_unit:parseInt(it.cost_per_unit)||0,
    bundle:it.bundle||null,
  }));


  const submit = async () => {
    if (!name.trim()) { alert('묶음 이름을 입력하세요.'); return; }
    if (items.length === 0) { alert('항목을 1개 이상 추가하세요.'); return; }
    const body = {
      name: name.trim(),
      description: description.trim() || null,
      items: cleanItems(),
      visibility,
    };
    setSaving(true);
    try {
      if (!isEdit) {
        await api.post('/bundles', body);
        navigate('/bundles');
      } else {
        await api.put(`/bundles/${id}`, body);
        navigate(`/bundles/${id}`);
      }
    } catch (e) { alert(e.message); }
    finally { setSaving(false); }
  };

  const headerTitle = !isEdit ? '새 묶음 만들기' : '묶음 수정';

  return (
    <div>
      {toast && <div style={{position:'fixed',top:20,left:'50%',transform:'translateX(-50%)',zIndex:9999,background:'#1e293b',color:'#fff',padding:'10px 24px',borderRadius:8,fontSize:13,fontWeight:600,boxShadow:'0 4px 12px rgba(0,0,0,0.15)'}}>{toast}</div>}

      <ItemPickerModal
        open={showItemPicker}
        onClose={() => setShowItemPicker(false)}
        products={products}
        parts={allParts}
        favorites={favorites}
        onToggleFavorite={toggleFavorite}
        onAddProduct={addProduct}
        onAddPart={addPart}
        hideBundles
      />

      <div className="card" style={{ padding:'14px 18px', marginBottom:14, display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        <span style={{ fontSize:16, fontWeight:700 }}>{headerTitle}</span>
        <div className="btn-group">
          <button className="btn btn-outline" onClick={()=>navigate(isEdit?`/bundles/${id}`:'/bundles')}>취소</button>
          {canEdit && <button className="btn btn-primary" disabled={saving} onClick={submit}>
            {saving ? '저장 중...' : (isEdit ? '💾 저장' : '✚ 만들기')}
          </button>}
        </div>
      </div>

      {/* 기본 정보 */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="card-title" style={{ marginBottom:12 }}>기본 정보</div>
        <div className="form-group"><label className="form-label">묶음 이름 *</label>
          <input className="form-input" value={name} onChange={e=>setName(e.target.value)} placeholder="예: eWalker V10 + 보드 2장 + 1년 유지보수" />
        </div>
        <div className="form-group"><label className="form-label">설명</label>
          <textarea className="form-input" rows={3} value={description} onChange={e=>setDescription(e.target.value)} placeholder="이 묶음의 사용 시나리오·고객 유형·할인 의도 등" />
        </div>
        <div className="form-group" style={{ marginBottom:0 }}><label className="form-label">공개 설정</label>
          <div style={{ display:'flex', gap:18, alignItems:'center' }}>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
              <input type="radio" name="vis" checked={visibility==='public'} onChange={()=>setVisibility('public')} /> 🌐 공개 (전사 공유)
            </label>
            <label style={{ display:'flex', alignItems:'center', gap:6, fontSize:13 }}>
              <input type="radio" name="vis" checked={visibility==='private'} onChange={()=>setVisibility('private')} /> 🔒 비공개 (나만 보기)
            </label>
          </div>
        </div>
      </div>

      {/* 구성 항목 — 견적 작성 표와 동일 형식 */}
      <div className="card" style={{ marginBottom:14 }}>
        <div className="card-header" style={{ marginBottom:10 }}>
          <div className="card-title">구성 항목 ({items.length}개)</div>
          <div className="btn-group">
            <button className="btn btn-outline btn-sm" onClick={()=>setShowItemPicker(true)} title="즐겨찾기 / 전체 제품·부품">⭐ 자주 쓰는 항목</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setShowAddProd(true);setAddSearch('');setAddType('all');}}>⚙ 상품 추가</button>
            <button className="btn btn-outline btn-sm" onClick={()=>{setShowAddPart(true);setAddSearch('');}}>▧ 부품 추가</button>
            <button className="btn btn-outline btn-sm" onClick={addManual}>✎ 수기입력 항목</button>
          </div>
        </div>

        <div style={{display:'flex',alignItems:'center',gap:22,marginBottom:14,padding:'8px 12px',background:'#f8fafc',borderRadius:8}}>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:'#64748b',cursor:'pointer'}}>소비자단가 <div className={`toggle ${showCP?'on':''}`} onClick={()=>{if(showCP)setShowCPA(false);setShowCP(!showCP);}}><div className="toggle-knob"></div></div></label>
          <label style={{display:'flex',alignItems:'center',gap:8,fontSize:12,color:showCP?'#64748b':'#cbd5e1',cursor:showCP?'pointer':'default'}}>소비자금액 <div className={`toggle ${showCPA&&showCP?'on':''}`} onClick={()=>{if(showCP)setShowCPA(!showCPA);}} style={!showCP?{opacity:0.4,pointerEvents:'none'}:{}}><div className="toggle-knob"></div></div></label>
        </div>

        <div style={{overflowX:'auto'}}>
          <table style={{fontSize:12,width:'100%',borderCollapse:'collapse'}}>
            <thead><tr style={{background:'#f8fafc'}}>
              <th style={{padding:'9px 10px',textAlign:'center',width:50,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>묶음</th>
              <th style={{padding:'9px 10px',textAlign:'left',minWidth:130,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>견적항목</th>
              <th style={{padding:'9px 10px',textAlign:'left',minWidth:260,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>세부사항</th>
              {showCP && <th style={{padding:'9px 10px',textAlign:'right',minWidth:100,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>소비자단가</th>}
              {showCP && showCPA && <th style={{padding:'9px 10px',textAlign:'right',minWidth:100,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>소비자금액</th>}
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:130,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>공급가액</th>
              <th style={{padding:'9px 10px',textAlign:'center',minWidth:60,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>수량</th>
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:120,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>공급금액</th>
              <th style={{padding:'9px 10px',textAlign:'right',minWidth:110,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>원가합계</th>
              <th style={{padding:'9px 10px',textAlign:'center',minWidth:70,fontWeight:600,fontSize:11,color:'#64748b',borderBottom:'2px solid #e2e8f0'}}>마진율</th>
              <th style={{width:30,borderBottom:'2px solid #e2e8f0'}}></th>
              <th style={{width:26,borderBottom:'2px solid #e2e8f0'}}></th>
            </tr></thead>
            <tbody>
              {(() => {
                const bundleMap = {};
                items.forEach((it, i) => {
                  if (!it.bundle) return;
                  if (!bundleMap[it.bundle]) bundleMap[it.bundle] = [];
                  bundleMap[it.bundle].push(i);
                });
                return items.map((it,idx) => {
                  const lt = pN(it.unit_price)*(parseInt(it.quantity)||1);
                  const ct = (parseInt(it.cost_per_unit)||0)*(parseInt(it.quantity)||1);
                  const mg = lt>0?(((lt-ct)/lt)*100).toFixed(1):'-';
                  const prod = it._product;
                  const hwParts = it.hw_specs?.custom_bom ? it.hw_specs.custom_bom.filter(b=>b.show_in_spec) : prod?.bom?.filter(b=>b.show_in_spec)||[];
                  const specs = it._editSpecs || it.specs || [];
                  const features = it._editFeatures || it.features || [];
                  const isEditing = editingDetail?.idx === idx;

                  const bGroup = it.bundle && bundleMap[it.bundle]?.length > 1 ? bundleMap[it.bundle] : null;
                  const isFirst = bGroup ? bGroup[0] === idx : true;
                  const showItemCell = !bGroup || isFirst;

                  return (
                    <tr key={idx} style={{borderBottom:'1px solid #f1f5f9',verticalAlign:'top'}}>
                      <td style={{padding:'8px 4px',textAlign:'center'}}>
                        {it._appliance_ref_id ? (
                          <span title={`Appliance 묶음 (분리 불가) — ${it._appliance_name || `#${it._appliance_ref_id}`}`}
                            style={{display:'inline-block',padding:'3px 6px',borderRadius:5,
                              background:'#dcfce7',color:'#15803d',fontSize:11,fontWeight:700}}>
                            🔒{it.bundle}
                          </span>
                        ) : (
                          <input style={{width:36,padding:'3px 0',border:'1px solid #e2e8f0',borderRadius:5,fontSize:12,textAlign:'center',outline:'none',background:bGroup?'#eff6ff':'transparent'}} value={it.bundle||''} onChange={e=>upItem(idx,'bundle',e.target.value)} />
                        )}
                      </td>
                      {showItemCell && <td style={{padding:10}} rowSpan={bGroup ? bGroup.length : 1}>
                        {it.item_type==='manual' ? (
                          <><textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,resize:'vertical',minHeight:36,...(!it.item_name?{borderColor:'#ef4444',background:'#fef2f2'}:{})}} rows={2} value={it.item_name} onChange={e=>upItem(idx,'item_name',e.target.value)} placeholder="견적항목명 *" /><div style={{marginTop:4}}><span className="badge badge-purple">수기</span></div></>
                        ) : (
                          <><div style={{fontWeight:700,fontSize:13,whiteSpace:'pre-wrap'}}>{it.item_model||it.item_name}</div><div style={{marginTop:4}}><span className={`badge ${it.item_type==='product'?'badge-blue':'badge-gray'}`}>{it.item_type==='product'?'상품':'부품'}</span></div></>
                        )}
                      </td>}

                      <td style={{padding:10,fontSize:11,lineHeight:1.8}}>
                        {it.item_type==='manual' ? (
                          <textarea style={{width:'100%',padding:'5px 8px',border:'1px solid #d1d5db',borderRadius:6,fontSize:11,resize:'vertical',minHeight:48,...(!it.description?{borderColor:'#ef4444',background:'#fef2f2'}:{})}} rows={3} value={it.description||''} onChange={e=>upItem(idx,'description',e.target.value)} placeholder="세부사항 *" />
                        ) : it.item_type==='product' && prod ? (
                          <>
                            {deriveDetailSections(it).map((s,i)=>(
                              <div key={`sec-${i}`} style={{marginBottom:4}}>
                                {s.title && <div style={{fontWeight:700,fontSize:12}}>{s.title}</div>}
                                {s.content && <div style={{fontSize:11,whiteSpace:'pre-wrap'}}>{s.content}</div>}
                              </div>
                            ))}
                            {specs.length>0 && (
                              <div style={{marginBottom:6}}>
                                <span style={{fontSize:10,fontWeight:700,color:'#94a3b8'}}>성능 <span style={{color:'#3b82f6',cursor:'pointer',fontSize:10}} onClick={()=>setEditingDetail({idx,section:'specs'})}>[수정]</span></span>
                                {isEditing && editingDetail.section==='specs' ? (
                                  <SpecEditor specs={specs} features={features} onSave={(s,f)=>saveDetailEdit(idx,s,f)} onCancel={()=>setEditingDetail(null)} section="specs" />
                                ) : specs.map((s,i)=><div key={i}><span style={{color:'#64748b',display:'inline-block',width:80}}>{s.key}</span>{s.value}</div>)}
                              </div>
                            )}
                            {hwParts.length>0 && (
                              <div style={{marginBottom:6}}>
                                <span style={{fontSize:10,fontWeight:700,color:'#94a3b8'}}>하드웨어사양 <span style={{color:'#3b82f6',cursor:'pointer',fontSize:10}} onClick={()=>setEditingHwIdx(idx)}>[수정]</span></span>
                                {hwParts.map((b,i)=><div key={i}>· {b.part_type||b.part_name}: {b.part_spec_display||b.part_spec}{(b.quantity||1)>1?` x${b.quantity}${b.part_unit||''}`:''}</div>)}
                              </div>
                            )}
                          </>
                        ) : it.item_type==='product' ? <span style={{color:'#94a3b8'}}>상품 상세 로딩 중...</span>
                          : <div style={{color:'#475569'}}>{it.description||it.item_name}</div>}
                      </td>

                      {showItemCell && showCP && <td style={{padding:10,textAlign:'right',color:'#94a3b8'}} rowSpan={bGroup ? bGroup.length : 1}>
                        <input style={{width:100,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:12,textAlign:'right',outline:'none'}} value={it.consumer_price?fmtN(it.consumer_price):''} onChange={e=>upItem(idx,'consumer_price',pN(e.target.value)||null)} placeholder="선택" />
                      </td>}
                      {showItemCell && showCP && showCPA && <td style={{padding:10,textAlign:'right',color:'#94a3b8'}} rowSpan={bGroup ? bGroup.length : 1}>{it.consumer_price?`₩${(Number(it.consumer_price)*(parseInt(it.quantity)||1)).toLocaleString()}`:'-'}</td>}

                      {showItemCell && <td style={{padding:10,textAlign:'right'}} rowSpan={bGroup ? bGroup.length : 1}>
                        <div style={{fontWeight:700,fontSize:13}}>₩{pN(it.unit_price).toLocaleString()}</div>
                        <input style={{width:120,padding:'5px 9px',border:'1px solid #d1d5db',borderRadius:6,fontSize:10,textAlign:'right',outline:'none',color:'#94a3b8'}} value={fmtN(it.unit_price)} onChange={e=>upItem(idx,'unit_price',String(pN(e.target.value)))} title="VAT 미포함 단가" />
                        {prod && <div style={{fontSize:10,color:'#94a3b8',marginTop:4,lineHeight:1.6}}>총판 ₩{Number(prod.price_distributor||0).toLocaleString()}<br/>협력사 ₩{Number(prod.price_partner||0).toLocaleString()}<br/>최종 ₩{Number(prod.price_end_user||0).toLocaleString()}</div>}
                      </td>}

                      {showItemCell && <td style={{padding:10,textAlign:'center'}} rowSpan={bGroup ? bGroup.length : 1}>
                        <input style={{width:50,padding:5,border:'1px solid #d1d5db',borderRadius:6,fontSize:13,textAlign:'center',outline:'none'}} value={it.quantity} onChange={e=>upItem(idx,'quantity',e.target.value)} />
                      </td>}
                      {showItemCell && <td style={{padding:10,textAlign:'right',fontWeight:700,fontSize:14}} rowSpan={bGroup ? bGroup.length : 1}>₩{lt.toLocaleString()}</td>}
                      <td style={{padding:10,textAlign:'right',position:'relative'}}>
                        <span className="cost-tooltip-wrap">
                          <span className="cost-value-hover">₩{ct.toLocaleString()}</span>
                          {prod && prod.bom && (
                            <div className="cost-tip">
                              <div style={{fontWeight:700,marginBottom:6}}>{it.item_name} BOM 원가 (단품)</div>
                              <table style={{width:'100%'}}><tbody>
                                {prod.bom.map((b,i)=><tr key={i}><td style={{border:'none',padding:'2px 0'}}>{b.part_name}</td><td style={{border:'none',padding:'2px 0',textAlign:'right',fontWeight:600}}>₩{Number(b.purchase_price||0).toLocaleString()} x{b.quantity}</td></tr>)}
                              </tbody></table>
                              <div style={{borderTop:'1px solid #e2e8f0',paddingTop:4,marginTop:4}}>단품: ₩{Number(it.cost_per_unit||0).toLocaleString()} x{it.quantity} = <strong>₩{ct.toLocaleString()}</strong></div>
                            </div>
                          )}
                        </span>
                      </td>
                      <td style={{padding:10,textAlign:'center',fontWeight:700,color:parseFloat(mg)>30?'#22c55e':parseFloat(mg)>0?'#f59e0b':'#ef4444'}}>{mg}%<div style={{fontSize:10,fontWeight:400}}>{parseFloat(mg)>30?'✓':'⚠'}</div></td>
                      {showItemCell && (() => {
                        const gFirst = bGroup ? bGroup[0] : idx;
                        const gLast  = bGroup ? bGroup[bGroup.length-1] : idx;
                        const canUp = gFirst > 0;
                        const canDown = gLast < items.length-1;
                        return (
                          <td style={{padding:'6px 2px',textAlign:'center',verticalAlign:'middle'}} rowSpan={bGroup ? bGroup.length : 1}>
                            <div style={{display:'flex',flexDirection:'column',gap:2,fontSize:11,lineHeight:1,userSelect:'none'}}>
                              <span title="위로" style={{cursor:canUp?'pointer':'default',opacity:canUp?0.7:0.2,color:'#64748b'}} onClick={()=>canUp && moveItem(idx,-1)}>▲</span>
                              <span title="아래로" style={{cursor:canDown?'pointer':'default',opacity:canDown?0.7:0.2,color:'#64748b'}} onClick={()=>canDown && moveItem(idx,1)}>▼</span>
                            </div>
                          </td>
                        );
                      })()}
                      <td style={{padding:10}}><span style={{color:'#ef4444',cursor:'pointer',fontSize:15,opacity:0.5}} onClick={()=>removeItem(idx)}>✕</span></td>
                    </tr>
                  );
                });
              })()}
              {items.length===0 && <tr><td colSpan={10+(showCP?1:0)+(showCPA&&showCP?1:0)} style={{textAlign:'center',padding:32,color:'#94a3b8'}}>상품 또는 부품을 추가하세요.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {/* 상품 추가 모달 */}
      {showAddProd && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setShowAddProd(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:28, width:1000, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>상품 추가</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={()=>setShowAddProd(false)}>✕</span>
            </div>
            <div style={{display:'flex',gap:6,marginBottom:10,flexWrap:'wrap'}}>
              {['all','software','license','hardware','appliance'].map(t=>(
                <button key={t} className={`btn btn-sm ${addType===t?'btn-primary':'btn-outline'}`} onClick={()=>setAddType(t)}>
                  {t==='all'?'전체':TYPE_LABEL[t]}
                </button>
              ))}
            </div>
            <div className="form-group"><input className="form-input" autoFocus value={addSearch} onChange={e=>setAddSearch(e.target.value)} placeholder="상품구분, 상품명, 모델명, 별칭 검색..." /></div>
            <table style={{ fontSize:12 }}><thead><tr>
              <th style={{width:60}}>유형</th><th>상품구분</th><th>상품명</th><th>모델명</th><th>별칭</th><th style={{textAlign:'right'}}>원가합계</th><th style={{width:60}}></th>
            </tr></thead><tbody>
              {products
                .filter(p=>addType==='all'||p.product_type===addType)
                .filter(p => matchAnyCI(addSearch, p.category, p.name, p.model, p.alias))
                .map(p=>(
                <tr key={p.id}>
                  <td><span className={`badge ${TYPE_BADGE[p.product_type]||'badge-gray'}`} title={TYPE_LABEL[p.product_type]||p.product_type}>{TYPE_SHORT[p.product_type]||p.product_type||'-'}</span></td>
                  <td><span className="badge badge-gray">{p.category}</span></td>
                  <td className="fw-bold">{p.name}</td>
                  <td>{p.model}</td>
                  <td>{p.alias || '-'}</td>
                  <td style={{textAlign:'right'}}>₩{Number(p.total_cost||0).toLocaleString()}</td>
                  <td><button className="btn btn-primary btn-sm" onClick={()=>addProduct(p)}>추가</button></td>
                </tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}

      {/* 부품 추가 모달 */}
      {showAddPart && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', zIndex:100, display:'flex', alignItems:'center', justifyContent:'center' }} onClick={()=>setShowAddPart(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:'#fff', borderRadius:14, padding:28, width:1000, maxWidth:'92vw', maxHeight:'80vh', overflowY:'auto' }}>
            <div style={{ fontSize:16, fontWeight:700, marginBottom:16, paddingBottom:10, borderBottom:'1px solid #e2e8f0', display:'flex', justifyContent:'space-between' }}>
              <span>부품 추가</span><span style={{ cursor:'pointer', color:'#94a3b8' }} onClick={()=>setShowAddPart(false)}>✕</span>
            </div>
            <div className="form-group"><input className="form-input" autoFocus value={addSearch} onChange={e=>setAddSearch(e.target.value)} placeholder="부품명, 코드 검색..." /></div>
            <table style={{ fontSize:12 }}><thead><tr><th>코드</th><th>부품명</th><th>규격</th><th style={{textAlign:'right'}}>매입가</th><th></th></tr></thead><tbody>
              {allParts.filter(p=>matchAnyCI(addSearch, p.name, p.code, p.spec)).map(p=>(
                <tr key={p.id}><td style={{fontFamily:'monospace'}}>{p.code}</td><td>{p.name}</td><td>{p.spec}</td><td style={{textAlign:'right'}}>₩{Number(p.purchase_price||0).toLocaleString()}</td><td><button className="btn btn-primary btn-sm" onClick={()=>addPart(p)}>추가</button></td></tr>
              ))}
            </tbody></table>
          </div>
        </div>
      )}

      {/* 하드웨어사양 편집 모달 */}
      {editingHwIdx !== null && items[editingHwIdx]?._product && (
        <HwSpecEditor
          item={items[editingHwIdx]}
          allParts={allParts}
          onSave={(customBom)=>saveHwSpec(editingHwIdx, customBom)}
          onCancel={()=>setEditingHwIdx(null)}
        />
      )}
    </div>
  );
}
